/**
 * @fileoverview System-web API client for online-only system API operations.
 * @module system-web/api-client
 *
 * Responsibilities:
 * - Attach bearer tokens to system API requests.
 * - Provide wrappers for online operations that cannot be fulfilled from Dexie alone.
 *
 * Dependencies:
 * - Browser Fetch and XMLHttpRequest APIs.
 * - Runtime token provider registered by authentication.
 *
 * Architecture:
 * - UI components should not call this client directly for normal data reads.
 * - Local-first services wrap this client only for inherently-online actions.
 *
 * Notes:
 * - API base URL resolution must remain environment/proxy driven; do not hardcode hosts.
 */

interface TokenProviderOptions {
  forceRefresh?: boolean;
}

type TokenProvider = (options?: TokenProviderOptions) => Promise<string | null>;

let _getToken: TokenProvider | undefined;



export function setTokenProvider(fn: TokenProvider) {
  _getToken = fn;
}

export async function getToken(): Promise<string | null> {
  return _getToken ? _getToken() : null;
}

async function buildJsonHeaders(options: RequestInit, forceRefresh = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (_getToken) {
    const token = await _getToken({ forceRefresh });
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

async function readApiError(res: Response): Promise<any> {
  const body = await res.json().catch(() => ({}));
  return createApiError(body, res.status);
}



/**
 * Default request timeout for online API calls. Generous because some system
 * operations (e.g. file parse, Regrid ingestion triggers) can be slow, but it
 * MUST be finite — without it a broken proxy or dropped connection leaves the
 * caller's promise pending forever (observed: stuck "Processing..." modal
 * after a vite proxy EPIPE with no response ever returned to the browser).
 *
 * Callers may override per-request by passing `options.signal` (their own
 * AbortController) — when present, the default timeout is NOT applied.
 */
const DEFAULT_API_TIMEOUT_MS = 60_000;

/**
 * AbortControllers for every in-flight apiFetch without a caller-supplied
 * signal. Aborted on pagehide/navigation so the browser does not report its
 * own cancel as an unhandled rejection (WebKit surfaces navigated-aborted
 * requests as "access control checks" pageerrors even when caught).
 */
const inflightControllers = new Set<AbortController>();
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    for (const controller of inflightControllers) controller.abort();
  });
}

/**
 * Wraps a fetch call with an AbortController-based timeout. If the caller
 * supplied `options.signal`, that signal is respected and no timeout is added
 * (the caller owns cancellation in that case).
 */
async function fetchWithTimeout(
  path: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  if (options.signal) {
    return fetch(path, options);
  }
  const controller = new AbortController();
  inflightControllers.add(controller);
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(path, { ...options, signal: controller.signal });
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeoutMs}ms: ${path}`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    inflightControllers.delete(controller);
  }
}

export async function apiFetch(path: string, options: RequestInit = {}): Promise<any> {

  const headers = await buildJsonHeaders(options);

  const res = await fetchWithTimeout(path, { ...options, headers }, DEFAULT_API_TIMEOUT_MS);


  if (!res.ok && (res.status === 401 || res.status === 403) && _getToken) {
    const retryHeaders = await buildJsonHeaders(options, true);
    const retryRes = await fetchWithTimeout(path, { ...options, headers: retryHeaders }, DEFAULT_API_TIMEOUT_MS);
    if (retryRes.ok) return retryRes.json();
    throw await readApiError(retryRes);
  }

  if (!res.ok) {
    throw await readApiError(res);

  }



  return res.json();

}

/**
 * @contract FormDataUploadProgress
 * Purpose: Byte progress emitted while the browser transfers multipart data.
 * Fields:  loaded, total, percent.
 * Rules:   percent is monotonic per request and clamped to 0-100.
 * Sync:    n/a — transient transport state.
 */
export interface FormDataUploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * @contract FormDataUploadOptions
 * Purpose: Optional progress callback for multipart uploads.
 * Fields:  onUploadProgress.
 * Rules:   Providing a callback selects XMLHttpRequest because Fetch does not
 *          expose browser upload progress events.
 * Sync:    n/a — request configuration.
 */
export interface FormDataUploadOptions {
  onUploadProgress?: (progress: FormDataUploadProgress) => void;
}

const FORM_DATA_TIMEOUT_MS = 300_000;

function createApiError(body: any, status: number): any {
  const err: any = new Error(body?.error || `Request failed: ${status}`);
  if (body?.validationErrors) err.validationErrors = body.validationErrors;
  err.status = status;
  err.apiBody = body;
  return err;
}

function postFormDataWithProgress<T>(
  path: string,
  formData: FormData,
  headers: Record<string, string>,
  onUploadProgress: (progress: FormDataUploadProgress) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.timeout = FORM_DATA_TIMEOUT_MS;
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);

    let maxPercent = 0;
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      const percent = Math.max(maxPercent, Math.min(100, Math.round((event.loaded / event.total) * 100)));
      maxPercent = percent;
      onUploadProgress({ loaded: event.loaded, total: event.total, percent });
    };
    xhr.onload = () => {
      let body: any = {};
      try { body = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { /* handled by status fallback */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else reject(createApiError(body, xhr.status));
    };
    xhr.onerror = () => reject(new Error(`Network error while uploading to ${path}`));
    xhr.ontimeout = () => reject(new Error(`Request timed out after ${FORM_DATA_TIMEOUT_MS}ms: ${path}`));
    xhr.onabort = () => reject(new Error(`Upload cancelled: ${path}`));
    xhr.send(formData);
  });
}

// POST FormData (multipart/form-data) — used for file uploads
export async function apiPostFormData<T>(
  path: string,
  formData: FormData,
  options: FormDataUploadOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};

  if (_getToken) {
    const token = await _getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  if (options.onUploadProgress) {
    try {
      return await postFormDataWithProgress(path, formData, headers, options.onUploadProgress);
    } catch (err: any) {
      if ((err?.status !== 401 && err?.status !== 403) || !_getToken) throw err;
      const token = await _getToken({ forceRefresh: true });
      const retryHeaders: Record<string, string> = {};
      if (token) retryHeaders.Authorization = `Bearer ${token}`;
      return postFormDataWithProgress(path, formData, retryHeaders, options.onUploadProgress);
    }
  }

  // File uploads can legitimately take longer than JSON calls; use a longer
  // timeout but still finite so a stalled upload surfaces an error instead of
  // hanging the UI forever.
  const res = await fetchWithTimeout(path, {
    method: 'POST',
    headers,
    body: formData,
  }, FORM_DATA_TIMEOUT_MS);

  if (!res.ok && (res.status === 401 || res.status === 403) && _getToken) {
    const token = await _getToken({ forceRefresh: true });
    const retryHeaders: Record<string, string> = {};
    if (token) retryHeaders.Authorization = `Bearer ${token}`;
    const retryRes = await fetchWithTimeout(path, {
      method: 'POST',
      headers: retryHeaders,
      body: formData,
    }, FORM_DATA_TIMEOUT_MS);
    if (retryRes.ok) return retryRes.json();
    throw await readApiError(retryRes);
  }

  if (!res.ok) throw await readApiError(res);
  return res.json();
}



export const api = {

  organizations: {

    /** @deprecated Use listOrganizations() from organizationService instead */
    list: () => apiFetch('/api/organizations'),

    /** @deprecated Use getOrganization(id) from organizationService instead */
    get: (id: string) => apiFetch(`/api/organizations/${id}`),

    create: (data: any) => apiFetch('/api/organizations', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/organizations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    updateStatus: (id: string, status: string) => apiFetch(`/api/organizations/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  },

  tenants: {

    /** @deprecated Use listTenants() from tenantService instead */
    list: (params?: Record<string, string>) => {

      const qs = params ? '?' + new URLSearchParams(params).toString() : '';

      return apiFetch(`/api/tenants${qs}`);

    },

    /** @deprecated Use getTenant(id) from tenantService instead */
    get: (id: string) => apiFetch(`/api/tenants/${id}`),

    create: (data: any) => apiFetch('/api/tenants', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    updateStatus: (id: string, status: string, extra?: any) => apiFetch(`/api/tenants/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status, ...extra }) }),

    /** @deprecated Use listTrials() from tenantService instead */
    trialsList: () => apiFetch('/api/tenants/trials/list'),
    trialExtend: (id: string, extensionDays: number, reason?: string) => apiFetch(`/api/tenants/${id}/trial/extend`, { method: 'PATCH', body: JSON.stringify({ extensionDays, reason }) }),
    trialConvert: (id: string, notes?: string) => apiFetch(`/api/tenants/${id}/trial/convert`, { method: 'PATCH', body: JSON.stringify({ notes }) }),
    trialMark: (id: string, durationDays: number, reason?: string) => apiFetch(`/api/tenants/${id}/trial/mark`, { method: 'PATCH', body: JSON.stringify({ durationDays, reason }) }),
    trialCleanup: (id: string, notes?: string) => apiFetch(`/api/tenants/${id}/trial/cleanup`, { method: 'POST', body: JSON.stringify({ notes }) }),
    trialExpireCheck: () => apiFetch('/api/tenants/trials/expire-check', { method: 'POST' }),
    /**
     * Retry a tenant that is stuck in `status: 'provisioning'` with a
     * recorded `provisioningError`. Idempotent on the server. Returns 202
     * Accepted \u2014 the caller should watch the tenant record (via sync) for
     * the final status transition.
     */
    retryProvisioning: (id: string) => apiFetch(`/api/tenants/${id}/retry-provisioning`, { method: 'POST' }),

    /**
     * Re-enqueue the Regrid parcel import job for a tenant whose
     * `regrid_import` provisioning step is `failed` or `skipped`. Returns
     * 202 Accepted — the caller should poll provisioning-status or watch
     * sync for the final job state.
     */
    retryRegridImport: (id: string) => apiFetch(`/api/tenants/${id}/retry-regrid-import`, { method: 'POST' }),

    /**
     * Actively verify the real state of each provisioning step (database,
     * code-set downloads, admin invite, redirect URIs). If all verifiable
     * steps are complete, the server reconciles the tenant to active.
     * Returns a ProvisioningStatusReport.
     */
    provisioningStatus: (id: string) => apiFetch(`/api/tenants/${id}/provisioning-status`),
    delete: (id: string) => apiFetch(`/api/tenants/${id}`, { method: 'DELETE' }),
  },

  users: {

    /** @deprecated Use listSystemUsers() from systemUserService instead */
    list: (params?: Record<string, string>) => {

      const qs = params ? '?' + new URLSearchParams(params).toString() : '';

      return apiFetch(`/api/users${qs}`);

    },

    /** @deprecated Use getSystemUser(id) from systemUserService instead */
    get: (id: string) => apiFetch(`/api/users/${id}`),

    create: (data: any) => apiFetch('/api/users', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    updateStatus: (id: string, status: string, extra?: any) => apiFetch(`/api/users/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status, ...extra }) }),

    addDeptMembership: (id: string, data: any) => apiFetch(`/api/users/${id}/memberships/department`, { method: 'POST', body: JSON.stringify(data) }),

    addOrgMembership: (id: string, data: any) => apiFetch(`/api/users/${id}/memberships/organization`, { method: 'POST', body: JSON.stringify(data) }),

    getLoginHistory: (id: string, params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiFetch(`/api/users/${id}/login-history${qs}`);
    },

  },

  lookups: {

    list: (params?: Record<string, string>) => {

      const qs = params ? '?' + new URLSearchParams(params).toString() : '';

      return apiFetch(`/api/lookups${qs}`);

    },

    types: () => apiFetch('/api/lookups/types'),

    get: (id: string) => apiFetch(`/api/lookups/${id}`),

    create: (data: any) => apiFetch('/api/lookups', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/lookups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  },

  systemRoles: {

    /** @deprecated Use listSystemRoles() from systemRoleService instead */
    list: () => apiFetch('/api/system-roles'),

    /** @deprecated Use getSystemRole(id) from systemRoleService instead */
    get: (id: string) => apiFetch(`/api/system-roles/${id}`),

    create: (data: any) => apiFetch('/api/system-roles', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/system-roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    updateStatus: (id: string, status: string) => apiFetch(`/api/system-roles/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

    permissionDefinitions: () => apiFetch('/api/system-roles/permissions/definitions'),

  },

  organizationRoles: {

    /** @deprecated Use listOrganizationRoles() from systemRoleService instead */
    list: (params?: Record<string, string>) => {

      const qs = params ? '?' + new URLSearchParams(params).toString() : '';

      return apiFetch(`/api/organization-roles${qs}`);

    },

    /** @deprecated Use getOrganizationRole(id) from systemRoleService instead */
    get: (id: string) => apiFetch(`/api/organization-roles/${id}`),

    create: (data: any) => apiFetch('/api/organization-roles', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/organization-roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    updateStatus: (id: string, status: string) => apiFetch(`/api/organization-roles/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

    permissionDefinitions: () => apiFetch('/api/organization-roles/permissions/definitions'),

  },

  userGroups: {

    list: (params?: Record<string, string>) => {

      const qs = params ? '?' + new URLSearchParams(params).toString() : '';

      return apiFetch(`/api/user-groups${qs}`);

    },

    get: (id: string) => apiFetch(`/api/user-groups/${id}`),

    create: (data: any) => apiFetch('/api/user-groups', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) => apiFetch(`/api/user-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    updateStatus: (id: string, status: string) => apiFetch(`/api/user-groups/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

    addMember: (id: string, data: any) => apiFetch(`/api/user-groups/${id}/members`, { method: 'POST', body: JSON.stringify(data) }),

    removeMember: (id: string, userId: string) => apiFetch(`/api/user-groups/${id}/members/${userId}`, { method: 'DELETE' }),

  },

  tiers: {
    /** @deprecated Use listTiers() from tierService instead */
    list: () => apiFetch('/api/tiers'),
    /** @deprecated Use getTier(idOrCode) from tierService instead */
    get: (idOrCode: string) => apiFetch(`/api/tiers/${idOrCode}`),
    update: (id: string, data: any) => apiFetch(`/api/tiers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    /** @deprecated Use compareTiers() from tierService instead */
    compare: (code1: string, code2: string) => apiFetch(`/api/tiers/${code1}/compare/${code2}`),
  },

  featureFlags: {
    /** @deprecated Use listFeatureFlags() from featureFlagService instead */
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiFetch(`/api/feature-flags${qs}`);
    },
    /** @deprecated Use getFeatureFlag(keyOrId) from featureFlagService instead */
    get: (keyOrId: string) => apiFetch(`/api/feature-flags/${keyOrId}`),
    create: (data: any) => apiFetch('/api/feature-flags', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiFetch(`/api/feature-flags/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateStatus: (id: string, status: string) => apiFetch(`/api/feature-flags/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    tenantOverrides: (tenantId: string) => apiFetch(`/api/feature-flags/tenant/${tenantId}`),
    setTenantOverride: (tenantId: string, flagKey: string, data: any) => apiFetch(`/api/feature-flags/tenant/${tenantId}/${flagKey}`, { method: 'PUT', body: JSON.stringify(data) }),
    removeTenantOverride: (tenantId: string, flagKey: string) => apiFetch(`/api/feature-flags/tenant/${tenantId}/${flagKey}`, { method: 'DELETE' }),
    evaluate: (tenantId: string) => apiFetch(`/api/feature-flags/evaluate/${tenantId}`),
  },

  modules: {
    /** @deprecated Use listModules() from moduleService instead */
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiFetch(`/api/modules${qs}`);
    },
    /** @deprecated Use getModule(codeOrId) from moduleService instead */
    get: (codeOrId: string) => apiFetch(`/api/modules/${codeOrId}`),
    create: (data: any) => apiFetch('/api/modules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiFetch(`/api/modules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateStatus: (id: string, status: string) => apiFetch(`/api/modules/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    tenantOverrides: (tenantId: string) => apiFetch(`/api/modules/tenant/${tenantId}`),
    setTenantOverride: (tenantId: string, moduleCode: string, data: any) => apiFetch(`/api/modules/tenant/${tenantId}/${moduleCode}`, { method: 'PUT', body: JSON.stringify(data) }),
    evaluate: (tenantId: string) => apiFetch(`/api/modules/evaluate/${tenantId}`),
  },

  auditLogs: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiFetch(`/api/audit-logs${qs}`);
    },
    get: (id: string) => apiFetch(`/api/audit-logs/${id}`),
  },

  lookupResolver: {
    resolve: (params: Record<string, string>) => {
      const qs = '?' + new URLSearchParams(params).toString();
      return apiFetch(`/api/lookup-resolver/resolve${qs}`);
    },
    resolveGrouped: (params: Record<string, string>) => {
      const qs = '?' + new URLSearchParams(params).toString();
      return apiFetch(`/api/lookup-resolver/resolve-grouped${qs}`);
    },
    typesSummary: () => apiFetch('/api/lookup-resolver/types-summary'),
  },

  invitations: {
    status: () => apiFetch('/api/invitations/status'),
    invite: (data: any) => apiFetch('/api/invitations', { method: 'POST', body: JSON.stringify(data) }),
    resend: (userId: string, data: any) => apiFetch(`/api/invitations/${userId}/resend`, { method: 'POST', body: JSON.stringify(data) }),
    revoke: (userId: string, data: any) => apiFetch(`/api/invitations/${userId}/revoke`, { method: 'POST', body: JSON.stringify(data) }),
  },

  onboardingDrafts: {
    /**
     * Server-side step validation. The frontend also validates locally via
     * validateWizardStep() in validateStep.ts \u2014 prefer local validation first.
     *
     * All other draft CRUD (list/get/create/update/delete) was removed as
     * part of the local-first migration: use onboardingDraftService.ts which
     * reads/writes Dexie directly and replicates through the sync pipeline.
     */
    validateStep: (id: string, step: number, data: any) =>
      apiFetch(`/api/onboarding-drafts/${id}/validate-step`, {
        method: 'POST',
        body: JSON.stringify({ step, data }),
      }),
  },

  onboarding: {
    /** POST /api/onboarding/provision — the only API call in the wizard.
     *  Sends the full wizard state; server creates tenant DB, enqueues migration + welcome email. */
    provision: (draftId: string, wizardState: any, isTrial?: boolean, tenantId?: string) =>
      apiFetch('/api/onboarding/provision', {
        method: 'POST',
        body: JSON.stringify({ draftId, wizardState, isTrial, tenantId }),
      }),
    /** GET /api/neris/lookup/:nerisId — verify a NERIS ID against local fireBoundaries */
    nerisLookup: (nerisId: string) => apiFetch(`/api/neris/lookup/${encodeURIComponent(nerisId)}`),
    /** GET /api/neris/search?q=name&state=AZ — search local fireBoundaries by name (Dexie fallback) */
    nerisSearch: (q: string, state?: string) => apiFetch(`/api/neris/search?q=${encodeURIComponent(q)}${state ? `&state=${encodeURIComponent(state)}` : ''}`),
    /** GET /api/neris/entity-search?q=name&state=AZ — live NERIS API entity search (OAuth2) */
    nerisEntitySearch: (q: string, state?: string) => apiFetch(`/api/neris/entity-search?q=${encodeURIComponent(q)}${state ? `&state=${encodeURIComponent(state)}` : ''}`),
    /** GET /api/neris/polygon/:nerisId — fetch jurisdiction polygon from NERIS pygeoapi (requires regionUrl) */
    nerisPolygon: (nerisId: string, regionUrl?: string) =>
      apiFetch(`/api/neris/polygon/${encodeURIComponent(nerisId)}${regionUrl ? `?regionUrl=${encodeURIComponent(regionUrl)}` : ''}`),
    /** POST /api/neris/refresh-tenant/:tenantId — live NERIS lookup + polygon fetch + tenant document update */
    nerisRefreshTenant: (tenantId: string) =>
      apiFetch(`/api/neris/refresh-tenant/${encodeURIComponent(tenantId)}`, { method: 'POST' }),
    /** POST /api/validate/email — server-side DNS/MX record check */
    validateEmail: (email: string) => apiFetch('/api/validate/email', { method: 'POST', body: JSON.stringify({ email }) }),
    /** GET /api/maps/geocode — external Mapbox proxy */
    geocode: (address: string) => apiFetch(`/api/maps/geocode?address=${encodeURIComponent(address)}`),
  },

  legacy: {
    /** GET /api/legacy/databases — list configured legacy SQL database names */
    databases: () => apiFetch('/api/legacy/databases'),
    /** GET /api/legacy/:database/departments — list departments in a legacy database */
    departments: (database: string) => apiFetch(`/api/legacy/${encodeURIComponent(database)}/departments`),
    /** GET /api/legacy/:database/departments/:id — get a single department */
    department: (database: string, departmentId: number) => apiFetch(`/api/legacy/${encodeURIComponent(database)}/departments/${departmentId}`),
  },

  systemEntry: {
    impersonate: (tenantId: string, themeId?: string) =>
      apiFetch('/api/system/entry/impersonate', { method: 'POST', body: JSON.stringify({ tenantId, themeId }) }),
    mobileImpersonate: (tenantId: string, themeId?: string) =>
      apiFetch('/api/system/entry/mobile-impersonate', { method: 'POST', body: JSON.stringify({ tenantId, themeId }) }),
  },

  auth: {
    config: (slug?: string) => apiFetch(`/api/auth/config${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`),
    tenantConfig: (slug: string) => apiFetch(`/api/auth/tenant-config/${encodeURIComponent(slug)}`),
    me: () => apiFetch('/api/auth/me'),
    createSessionToken: (data: { entraUserId: string; email: string; departmentSlug: string }) => 
      apiFetch('/api/auth/create-session-token', { method: 'POST', body: JSON.stringify(data) }),
  },

  codeSetVendors: {
    getRegulatory: (tenantId: string) =>
      apiFetch(`/api/tenants/${tenantId}/regulatory`),
    upsertVendor: (tenantId: string, data: Record<string, unknown>) =>
      apiFetch(`/api/tenants/${tenantId}/regulatory/vendors`, { method: 'PUT', body: JSON.stringify(data) }),
    validateVendor: (tenantId: string, vendorId: string, data?: Record<string, unknown>) =>
      apiFetch(`/api/tenants/${tenantId}/regulatory/vendors/${vendorId}/validate`, {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      }),
    getCatalog: (tenantId: string, vendorId: string) =>
      apiFetch(`/api/tenants/${tenantId}/regulatory/vendors/${vendorId}/catalog`),
    triggerDownload: (tenantId: string, data: { vendorId: string; bookId: string; edition: string; title: string; operation?: 'download' | 'rehydrate' }) =>
      apiFetch(`/api/tenants/${tenantId}/code-set-download`, { method: 'POST', body: JSON.stringify(data) }),
    downloadStatus: (tenantId: string, jobId: string) =>
      apiFetch(`/api/tenants/${tenantId}/code-set-download/${jobId}`),

    /**
     * Pre-tenant ICC credential validation. Used by the create-tenant
     * wizard before any tenant document exists. Credentials are NEVER
     * persisted by the server.
     */
    preflightValidateIcc: (data: { clientId: string; clientSecret: string }): Promise<{ valid: boolean; message?: string }> =>
      apiFetch('/api/code-set/preflight/icc/validate', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /**
     * Pre-tenant ICC catalog fetch. Returns the list of books this client's
     * subscription has access to so the wizard can show selection checkboxes.
     */
    preflightCatalogIcc: (data: { clientId: string; clientSecret: string }): Promise<{
      books: Array<{ bookId: string; title: string; edition: string; chapterCount?: number }>;
    }> =>
      apiFetch('/api/code-set/preflight/icc/catalog', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  importTemplates: {
    create: (data: any) => apiFetch('/api/system/import-templates', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiFetch(`/api/system/import-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiFetch(`/api/system/import-templates/${id}`, { method: 'DELETE' }),
    clone: (id: string, name?: string) => apiFetch(`/api/system/import-templates/${id}/clone`, { method: 'POST', body: JSON.stringify({ name }) }),
    setVisibility: (id: string, data: { visibility: string; allowedTenantIds?: string[] }) =>
      apiFetch(`/api/system/import-templates/${id}/visibility`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  aiModels: {
    list: () => apiFetch('/api/system/ai-models'),
    create: (input: any) => apiFetch('/api/system/ai-models', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, patch: any) => apiFetch(`/api/system/ai-models/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (id: string) => apiFetch(`/api/system/ai-models/${id}`, { method: 'DELETE' }),
    discover: () => apiFetch('/api/system/ai-models/discover'),
    seed: (models: any[]) => apiFetch('/api/system/ai-models/seed', { method: 'POST', body: JSON.stringify({ models }) }),
  },

  aiTaskAssignments: {
    list: () => apiFetch('/api/system/ai-task-assignments'),
    update: (taskKey: string, data: { modelId?: string; isActive?: boolean }) =>
      apiFetch(`/api/system/ai-task-assignments/${taskKey}`, { method: 'PATCH', body: JSON.stringify(data) }),
    seed: (taskAssignments: any[]) => apiFetch('/api/system/ai-task-assignments/seed', { method: 'POST', body: JSON.stringify({ taskAssignments }) }),
  },

  clusters: {
    list: () => apiFetch('/api/clusters'),
    create: (data: any) => apiFetch('/api/clusters', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiFetch(`/api/clusters/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    testConnection: (data: { clusterHost?: string; connectionString?: string }) =>
      apiFetch('/api/clusters/test-connection', { method: 'POST', body: JSON.stringify(data) }),
  },

  // #737 Phase 6 — inspection-program + rule-catalog admin (plan §11.5).
  inspectionPrograms: {
    list: () => apiFetch('/api/system-inspection-programs'),
    create: (data: any) => apiFetch('/api/system-inspection-programs', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, patch: any) => apiFetch(`/api/system-inspection-programs/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
    setActive: (id: string, active: boolean) =>
      apiFetch(`/api/system-inspection-programs/${id}/active`, { method: 'PATCH', body: JSON.stringify({ active }) }),
    propagate: (scope?: 'inspectionPrograms' | 'scheduleRuleCatalog' | 'all') =>
      apiFetch('/api/inspection-programs/propagate', { method: 'POST', body: JSON.stringify({ scope: scope ?? 'all' }) }),
  },

  scheduleRuleCatalog: {
    list: () => apiFetch('/api/schedule-rule-catalog'),
    create: (data: any) => apiFetch('/api/schedule-rule-catalog', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, patch: any) => apiFetch(`/api/schedule-rule-catalog/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  },

};

